import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { Typography, TextField, IconButton, ListItemText, List, ListItem } from '@mui/material';
import { Delete, CloudUpload } from '@mui/icons-material';
import { InfoText, Loader, StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import { acceptedUploadFileTypes, bytesToMb, common, convertBytesToMb } from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks';
import { acceptedUploadFileTypesArray, riskTypeLife, translations } from '../../lib/consts';
import { getDocList, submitDocuments } from '../../rpc/actions';
import { prepareDocumentUploadFormData } from '../../lib/helpers';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
};

const MAX_SIZE = 25;
const MAX_FILE_SIZE = 20;

const DocumentationScreen = ({ navigator }: Props) => {
    const { values, setFieldValue } = useFormikContext<any>();
    const [currentStep, { goForward, goBack }] = navigator;
    const [fileExceedsAllowedSize, setFileExceedsAllowedSize] = useState('');
    const [fileOverLimitSize, setFileOverLimitSize] = useState('');

    const id = values.tempID;
    const data = prepareDocumentUploadFormData({ id, documents: values.uploadDocList });
    const { response: documentsList, fetchMethod: getDocuments } = useApi(() => getDocList(id));
    const { response: documents, fetchMethod: postDocuments } = useApi(() => submitDocuments(data), goForward);

    useEffect(() => {
        if (values.tempID && values.tempID !== '') getDocuments();
    }, [values.tempID]);

    const handleNext = (): void => {
        postDocuments();
    };

    const handleGoBack = (): void => {
        setFieldValue('submittedSuccessfully', false);
        goBack();
    };

    const totalUploadSize = () => {
        let size = 0;
        if (values.uploadDocList.length > 0) {
            size = values.uploadDocList
                .map((item: any) => item.value.size)
                .reduce((totalSize: number, currentSize: number) => totalSize + currentSize, 0);

            return convertBytesToMb(size);
        }
        return size;
    };

    const addFilesToDocList = (field: string, event: any, documentId: number) => {
        const listOfExistsFile: string[] = [];
        const listOfNotValidExtFile: string[] = [];
        const listOfLargeFile: string[] = [];
        const listOfOverLimitFile: string[] = [];
        setFileExceedsAllowedSize('');
        setFileOverLimitSize('');
        for (let index = 0; index < event.target.files.length; index++) {
            const value = event.target.files[index];
            const sizeMB = convertBytesToMb(value.size);
            const sizeWithCurrentFile = totalUploadSize() + sizeMB;
            if (sizeMB <= MAX_FILE_SIZE && sizeWithCurrentFile <= MAX_SIZE) {
                const docList = values.uploadDocList;
                let fileExists = false;
                let fileAlreadyUploaded = false;

                for (let i = 0; i < docList.length; i++) {
                    if (docList[i].value && docList[i].value.name === value.name) {
                        // A file with the same name already exists in the docList
                        fileExists = true;
                        listOfExistsFile.push(value.name);
                        break;
                    }
                    if (
                        docList[i].value &&
                        docList[i].value.lastModified === value.lastModified &&
                        docList[i].value.size === value.size
                    ) {
                        // A file with the same last modified timestamp already exists in the docList
                        fileAlreadyUploaded = true;
                        listOfExistsFile.push(value.name);
                        break;
                    }
                }

                const extFile = value.name.substring(value.name.lastIndexOf('.') + 1).toLowerCase();
                const validExtFile = acceptedUploadFileTypesArray.includes(extFile);
                if (!validExtFile) {
                    listOfNotValidExtFile.push(value.name);
                }

                if (!fileExists && !fileAlreadyUploaded && validExtFile) {
                    docList.push({
                        field,
                        value,
                        documentId,
                    });
                    setFieldValue('uploadDocList', docList);
                }
            } else {
                if (sizeMB > MAX_FILE_SIZE) {
                    listOfLargeFile.push(value.name);
                } else if (sizeWithCurrentFile > MAX_SIZE) {
                    listOfOverLimitFile.push(value.name);
                }
            }
        }
        if (listOfLargeFile.length > 0) {
            setFileExceedsAllowedSize(`(${listOfLargeFile.join(', ')})`);
        }
        if (listOfOverLimitFile.length > 0) {
            setFileOverLimitSize(`(${listOfOverLimitFile.join(', ')})`);
        }

        const errorMsgText =
            listOfExistsFile.length > 0
                ? `${translations.documentation.FILE_ALREADY_UPLOADED} (${listOfExistsFile.join(', ')})`
                : listOfNotValidExtFile.length > 0
                ? `${translations.documentation.FORMAT_NOT_ALLOWED} (${listOfNotValidExtFile.join(', ')})`
                : null;
        setFieldValue('errorMsgText', errorMsgText);
        event.target.value = null;
    };

    const deleteUploadedFile = (option: string) => {
        const resultUploadDocList = values.uploadDocList.filter((item: { field: string; value: File }) => {
            const pom = item.value.name !== option;
            return pom;
        });
        setFieldValue('uploadDocList', resultUploadDocList);
        setFieldValue('errorMsgText', null);
        setFileExceedsAllowedSize('');
        setFileOverLimitSize('');
    };

    const renderUploadDoc = (item: any) => {
        const { id, naziv, opis } = item;
        const documentId = id;
        return (
            <div key={id}>
                <List sx={{ paddingBottom: 0, paddingTop: 0 }}>
                    <Typography sx={{ color: '#0000009c', fontWeight: '600' }} paddingTop={4}>
                        {naziv}:
                    </Typography>
                    {opis ? <Typography>{opis}</Typography> : null}
                    {values.uploadDocList.length !== 0 &&
                        values.uploadDocList.map((file: any, index: number) => {
                            if (file.field.includes(naziv)) {
                                return (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            border: '2px solid lightgrey',
                                            borderRadius: '5px',
                                            margin: '10px 0',
                                            padding: '3px 8px',
                                        }}
                                    >
                                        <ListItemText primary={file.value.name} />
                                        {bytesToMb(file.value.size, 1)}
                                        {translations.documentation.DOCUMENTATION_MB}
                                        <Delete
                                            color="primary"
                                            onClick={() => deleteUploadedFile(file.value.name)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </ListItem>
                                );
                            }
                        })}
                </List>
                <TextField
                    variant="outlined"
                    disabled={true}
                    style={{ background: 'gray', borderRadius: '5px', width: '100%' }}
                    InputProps={{
                        startAdornment: (
                            <IconButton
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    right: '0',
                                    left: '0',
                                    height: '56px',
                                    borderRadius: '5px',
                                    margin: '0 auto',
                                }}
                            >
                                <CloudUpload color="error" style={{ fontSize: '30', marginRight: '20' }} />
                                <p
                                    style={{
                                        color: 'white',
                                        textTransform: 'uppercase',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                    }}
                                >
                                    {translations.documentation.CHOOSE_DOC}
                                </p>
                                <input
                                    accept={acceptedUploadFileTypes}
                                    type="file"
                                    multiple
                                    style={{
                                        opacity: '0',
                                        position: 'absolute',
                                        height: '48px',
                                        cursor: 'pointer',
                                        width: '100%',
                                    }}
                                    onChange={(event) => {
                                        addFilesToDocList(`documents.${naziv}`, event, documentId);
                                    }}
                                />
                            </IconButton>
                        ),
                    }}
                />
            </div>
        );
    };

    return (
        <>
            <StepWrapper>
                <Loader openLoader={documents.isLoading}></Loader>
                <StyledCardSection>
                    <Typography style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: 0 }} variant="caption">
                        {translations.documentation.ADD_DOC}
                    </Typography>

                    <div className="allowed-files-container">
                        <Typography style={{ display: 'flex', justifyContent: 'center' }}>
                            {translations.documentation.TOTAL_UPLOAD_SIZE_LABEL}
                            {MAX_SIZE}
                            {translations.documentation.DOCUMENTATION_MB_SPACE}
                        </Typography>
                        <Typography style={{ display: 'flex', justifyContent: 'center' }}>
                            {translations.documentation.TOTAL_UPLOAD_SIZE_LEFT_LABEL}
                            {Math.floor(MAX_SIZE - totalUploadSize())}
                            {translations.documentation.DOCUMENTATION_MB_SPACE}
                        </Typography>
                        <Typography style={{ display: 'flex', justifyContent: 'center' }}>
                            {translations.documentation.ALLOWED_FILE_FORMATS}
                        </Typography>
                    </div>

                    {documentsList.isSuccess && documentsList.data.data.map((item: any) => renderUploadDoc(item))}
                    {fileExceedsAllowedSize !== '' && (
                        <p style={{ display: 'flex', justifyContent: 'center', color: '#c21b17' }}>
                            {`${translations.documentation.EXEEEDS_ALLOWED_SIZE} ${fileExceedsAllowedSize}`}
                        </p>
                    )}
                    {fileOverLimitSize !== '' && (
                        <p style={{ display: 'flex', justifyContent: 'center', color: '#c21b17' }}>
                            {`${translations.documentation.TOTAL_UPLOAD_SIZE} ${fileOverLimitSize}`}
                        </p>
                    )}
                    {values.errorMsgText && (
                        <p style={{ display: 'flex', justifyContent: 'center', color: '#c21b17' }}>
                            {values.errorMsgText}
                        </p>
                    )}

                    {!values.risks.includes(riskTypeLife.outlive) && (
                        <InfoText label={translations.documentation.NOTICE}></InfoText>
                    )}
                    <InfoText label={translations.documentation.ADDITIONAL_DOC}></InfoText>
                </StyledCardSection>
                <StepperControls onPrevious={handleGoBack} onNext={handleNext} onNextLabel={common.NEXT} />
            </StepWrapper>
        </>
    );
};

export default DocumentationScreen;
